import type { ReactElement } from "react";

import {
  TransitionAnimationProvider,
  type TSwitchAnimation,
  type TTransitionAnimationOptions,
} from "context/transitionAnimationContext";
import { List } from "immutable";
import { useEffect, useRef, useState } from "react";
import { type Location, Routes, useLocation } from "react-router";
import requestRenderFrame, { type RenderFrameHandle } from "utils/ui/requestRenderFrame";

const SECOND_IN_MS = 1000;
const UNMOUNT_TIMEOUT = SECOND_IN_MS * 1.5;
const RENDER_FRAME_TIMEOUT = SECOND_IN_MS * 0.5;

type TRenderLocation = {
  addTime: number;
  contextState: TTransitionAnimationOptions;
  location: Location;
};

type TSwitchTransitionsProps = {
  animation?: TSwitchAnimation;
  reverse?: boolean;
};

export const SwitchTransitions: FC<TSwitchTransitionsProps> = ({
  animation = "fade-move-from-center",
  children,
  reverse = false,
}) => {
  const location = useLocation();
  const [renderLocations, setRenderLocations] = useState(() => List<TRenderLocation>());
  const unmountTimeoutsRef = useRef<number[]>([]);

  useEffect(() => {
    // on the first time, add the location without animating it
    if (renderLocations.size === 0) {
      setRenderLocations(
        List([
          {
            addTime: Date.now(),
            contextState: {
              animate: false,
              animation,
              movement: "standstill",
              reverse,
            },
            location,
          },
        ]),
      );
    } else {
      // when there are already some locations in `renderLocations`,
      // animate the entering of the new location and the exiting of the previous location.
      // but only if they have a different pathnames
      // first, add new location to the dom and prepare the new and previous locations for an animation
      const lastItem = renderLocations.last();
      const samePathname = lastItem?.location.pathname === location.pathname;

      if (!!lastItem && !samePathname) {
        const outItem: TRenderLocation = {
          ...lastItem,
          contextState: {
            animate: false,
            animation,
            movement: "out",
            reverse,
          },
        };
        const inItem: TRenderLocation = {
          addTime: Date.now(),
          contextState: {
            animate: false,
            animation,
            movement: "in",
            reverse,
          },
          location,
        };

        // we should clear the previous transitions of the same location if they exist
        renderLocations.forEach((renderLocation, index) => {
          if (renderLocation.location.pathname === location.pathname) {
            window.clearTimeout(unmountTimeoutsRef.current.at(index));
            unmountTimeoutsRef.current.splice(index, 1);
          }
        });

        setRenderLocations(
          renderLocations
            .slice(0, -1)
            .filter((renderLocation) => renderLocation.location.pathname !== location.pathname)
            .push(outItem, inItem),
        );
      }

      const scheduleUnmount = () => {
        if (!samePathname) {
          unmountTimeoutsRef.current.push(
            window.setTimeout(() => {
              setRenderLocations((renderLocations) => renderLocations.shift());
              unmountTimeoutsRef.current.shift();
            }, UNMOUNT_TIMEOUT),
          );
        }
      };

      // wait until the items are added to the DOM and rendered
      let renderFrame: null | RenderFrameHandle = requestRenderFrame(() => {
        // trigger the transition animations
        setRenderLocations((renderLocations) => {
          const oldOutItem = renderLocations.get(-2);
          const oldInItem = renderLocations.last();
          if (renderLocations.size < 2 || !oldOutItem || !oldInItem) {
            if (!oldInItem) return renderLocations;
            return renderLocations.setIn([-1], { ...oldInItem, location });
          }
          // keep the animation/reverse each item was created with — re-resolving them here
          // (e.g. on a same-pathname search-param navigation while the previous page is still
          // mounted) changes the css animation name and restarts the transition, flashing the
          // outgoing page back into view
          const outItem: TRenderLocation = {
            ...oldOutItem,
            contextState: {
              ...oldOutItem.contextState,
              animate: true,
            },
          };

          const inItem: TRenderLocation = {
            ...oldInItem,
            contextState: {
              ...oldInItem.contextState,
              animate: true,
            },
          };

          return renderLocations.slice(0, -2).push(outItem, inItem);
        });

        renderFrame = null;
        scheduleUnmount();
      }, RENDER_FRAME_TIMEOUT);

      return () => {
        if (renderFrame) {
          renderFrame.destroy();
          scheduleUnmount();
        }
      };
    }

    return () => undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Separate effect to handle search param changes on the same page
  useEffect(() => {
    if (renderLocations.size > 0) {
      setRenderLocations((currentLocations) => {
        // Only update locations that match the pathname AND are not currently transitioning
        // This prevents interfering with ongoing animations
        const hasChanges = currentLocations.some(
          (renderLocation) =>
            renderLocation.location.pathname === location.pathname
            && (renderLocation.contextState.movement === "standstill" || renderLocation.contextState.movement === "in"),
        );
        if (!hasChanges) return currentLocations;

        return currentLocations.map((renderLocation) => {
          if (
            renderLocation.location.pathname === location.pathname
            && (renderLocation.contextState.movement === "standstill" || renderLocation.contextState.movement === "in")
          ) {
            return { ...renderLocation, location };
          }
          return renderLocation;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.hash]);

  useEffect(() => {
    const current = unmountTimeoutsRef.current;
    return () => {
      while (current.length > 0) {
        window.clearTimeout(current.shift());
      }
    };
  }, []);

  return (
    <>
      {renderLocations.map<ReactElement<unknown>>((renderLocation) => {
        return (
          <TransitionAnimationProvider key={renderLocation.location.pathname} options={renderLocation.contextState}>
            <Routes location={renderLocation.location}>{children}</Routes>
          </TransitionAnimationProvider>
        );
      })}
    </>
  );
};
